import { useState, useEffect, useCallback, useRef } from "react";

interface Answers {
  age: number;
  savingsPct: number;
  cushion: number;
  investments: number;
  security: number;
  goal: number;
}

const SCAN_TEXTS = [
  "Analiza dochodów...",
  "Weryfikacja poduszki bezpieczeństwa...",
  "Ocena dywersyfikacji...",
  "Analiza zabezpieczenia na przyszłość...",
  "Generowanie rekomendacji...",
];

export default function FinancialAnalyzer() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<Partial<Answers>>({});
  const [isScanning, setIsScanning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [scanText, setScanText] = useState(SCAN_TEXTS[0]);
  const [scanProgress, setScanProgress] = useState(0);
  const [gaugeOffset, setGaugeOffset] = useState(283);
  const resultsRef = useRef<HTMLDivElement>(null);

  const totalSteps = 6;
  const progressPct = Math.round(((step - 1) / totalSteps) * 100);

  const handleSelect = useCallback((key: keyof Answers, value: number) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const nextStep = useCallback(() => {
    if (step < totalSteps) {
      setStep((s) => s + 1);
    } else {
      setIsScanning(true);
    }
  }, [step]);

  const prevStep = useCallback(() => {
    if (step > 1) setStep((s) => s - 1);
  }, [step]);

  // Scanning animation
  useEffect(() => {
    if (!isScanning) return;
    let idx = 0;
    const textInterval = setInterval(() => {
      idx++;
      if (idx < SCAN_TEXTS.length) {
        setScanText(SCAN_TEXTS[idx]);
      }
    }, 900);

    const progInterval = setInterval(() => {
      setScanProgress((p) => {
        if (p >= 100) {
          clearInterval(progInterval);
          clearInterval(textInterval);
          setTimeout(() => {
            setIsScanning(false);
            setShowResults(true);
          }, 400);
          return 100;
        }
        return p + 5;
      });
    }, 180);

    return () => {
      clearInterval(textInterval);
      clearInterval(progInterval);
    };
  }, [isScanning]);

  // Scroll to results
  useEffect(() => {
    if (showResults && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [showResults]);

  // Calculate score
  const calculateScore = useCallback(() => {
    const a = answers as Answers;
    // Map savingsPct (0-50) to 1-4
    const savingsScore = a.savingsPct <= 10 ? 1 : a.savingsPct <= 25 ? 2 : a.savingsPct <= 40 ? 3 : 4;
    const points = (a.age || 1) + savingsScore + (a.cushion || 1) + (a.investments || 1) + (a.security || 1) + (a.goal || 1);
    return Math.min(100, Math.round((points / 24) * 100));
  }, [answers]);

  // Animate gauge when results show
  useEffect(() => {
    if (!showResults) return;
    const score = calculateScore();
    const targetOffset = 283 - (score / 100) * 283;
    const timer = setTimeout(() => setGaugeOffset(targetOffset), 100);
    return () => clearTimeout(timer);
  }, [showResults, calculateScore]);

  const score = showResults ? calculateScore() : 0;

  const getScoreLabel = (s: number) => {
    if (s >= 80) return { label: "Doskonała kondycja", color: "bg-green-100 text-green-700" };
    if (s >= 60) return { label: "Dobra kondycja", color: "bg-blue-100 text-blue-700" };
    if (s >= 40) return { label: "Wymaga uwagi", color: "bg-amber-100 text-amber-700" };
    return { label: "Wymaga pilnej interwencji", color: "bg-red-100 text-red-700" };
  };

  const scoreInfo = getScoreLabel(score);

  // Insights generation
  const insights = (() => {
    const list: string[] = [];
    const a = answers as Answers;
    if (!a.cushion || a.cushion <= 2) {
      list.push("Twoja <b>poduszka bezpieczeństwa</b> jest niska. Zalecane minimum to 3–6 miesięcy wydatków. Warto priorytetowo odkładać na ten cel.");
    }
    if (!a.investments || a.investments <= 2) {
      list.push("<b>Oszczędności nie pracują</b> — pozostają na koncie lub lokacie. Rozważ obligacje, fundusze lub IKE/IKZE, by chronić kapitał przed inflacją.");
    }
    if (!a.security || a.security <= 2) {
      list.push("<b>Zabezpieczenie na przyszłość</b> jest podstawowe. Brak dodatkowej emerytury lub ochrony zdrowia naraża na ryzyko.");
    }
    if (!a.savingsPct || a.savingsPct < 15) {
      list.push("<b>Poziom oszczędzania</b> jest niski. Przy założeniu 10% dochodu odkładania, osiągnięcie celów finansowych zajmie znacznie dłużej.");
    }
    if (list.length === 0) {
      list.push("Twoja <b>sytuacja finansowa</b> jest solidna. Warto teraz skupić się na optymalizacji i pomnażaniu kapitału.");
      list.push("Rozważ <b>konsultację</b> z doradcą, by zbudować długoterminowy plan inwestycyjny.");
    }
    return list;
  })();

  // Result cards data
  const resCards = (() => {
    const a = answers as Answers;
    const savingsScore = a.savingsPct <= 10 ? 1 : a.savingsPct <= 25 ? 2 : a.savingsPct <= 40 ? 3 : 4;
    return [
      {
        name: "Oszczędzanie",
        val: `${a.savingsPct || 0}%`,
        note: a.savingsPct >= 20 ? "Dobry poziom odkładania" : "Warto zwiększyć do 15–20%",
        meter: (savingsScore / 4) * 100,
      },
      {
        name: "Poduszka bezpieczeństwa",
        val: a.cushion === 4 ? "6+ msc" : a.cushion === 3 ? "3–6 msc" : a.cushion === 2 ? "1–3 msc" : "<1 msc",
        note: a.cushion >= 3 ? "Wystarczająca rezerwa" : "Cel: min. 3 miesiące",
        meter: ((a.cushion || 1) / 4) * 100,
      },
      {
        name: "Inwestowanie",
        val: a.investments === 4 ? "Zdywersyfikowane" : a.investments === 3 ? "Częściowe" : a.investments === 2 ? "Lokaty" : "Brak",
        note: a.investments >= 3 ? "Kapitał rośnie" : "Warto wprowadzić inwestycje",
        meter: ((a.investments || 1) / 4) * 100,
      },
      {
        name: "Zabezpieczenie",
        val: a.security === 4 ? "Pełne" : a.security === 3 ? "Częściowe" : a.security === 2 ? "Podstawowe" : "Brak",
        note: a.security >= 3 ? "Dobra ochrona" : "Rozważ dodatkową polisę i emeryturę",
        meter: ((a.security || 1) / 4) * 100,
      },
    ];
  })();

  const canProceed = (() => {
    switch (step) {
      case 1: return answers.age !== undefined;
      case 2: return answers.savingsPct !== undefined;
      case 3: return answers.cushion !== undefined;
      case 4: return answers.investments !== undefined;
      case 5: return answers.security !== undefined;
      case 6: return answers.goal !== undefined;
      default: return false;
    }
  })();

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950 py-20 md:py-24 lg:py-28">
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full bg-primary-500/5 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-[640px] mx-auto px-6 text-center">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-5 animate-fade-in-up">
            Bezpłatna analiza · 100% anonimowo
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-[2.8rem] font-bold leading-[1.15] text-white mb-5 animate-fade-in-up animation-delay-100">
            Sprawdź swoją kondycję finansową w 60 sekund
          </h1>
          <p className="text-base text-white/60 leading-[1.7] mb-8 max-w-[540px] mx-auto animate-fade-in-up animation-delay-200">
            Odpowiedz na 6 krótkich pytań — bez podawania jakichkolwiek danych osobowych.
            Otrzymasz natychmiastowy wynik wraz ze wskazaniem obszarów, które warto omówić z doradcą.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10 animate-fade-in-up animation-delay-300">
            <button
              onClick={() => document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-line" />
              Rozpocznij analizę
            </button>
            <a
              href="tel:698084044"
              className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 border-2 border-white/20 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/40 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              Zadzwoń i umów konsultację
            </a>
          </div>
          <div className="flex flex-wrap gap-5 justify-center animate-fade-in-up animation-delay-400">
            {["Bez podawania danych", "Wynik natychmiast", "Bez zobowiązań"].map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm text-white/50">
                <i className="ri-check-line text-green-400 text-xs" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Analyzer */}
      <section id="analyzer" className="w-full bg-background-100 py-16 md:py-24">
        <div className="px-6 md:px-12 max-w-[660px] mx-auto">
          {/* Section head */}
          {!showResults && !isScanning && (
            <div className="text-center mb-10">
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-500 mb-3 block">Analizator</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Analiza kondycji finansowej</h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                6 pytań, które porządkują obraz Twoich finansów — od poduszki bezpieczeństwa po plan na przyszłość.
              </p>
            </div>
          )}

          {/* Panel */}
          <div className="bg-background-50 rounded-2xl border border-background-200/60 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_12px_32px_rgba(15,23,42,0.06)] overflow-hidden">
            {/* Progress header */}
            {!showResults && !isScanning && (
              <div className="px-6 md:px-8 pt-5">
                <div className="flex justify-between text-[11px] font-semibold text-foreground-500 mb-2.5">
                  <span>Krok {step} z {totalSteps}</span>
                  <span>{progressPct}%</span>
                </div>
                <div className="h-[5px] bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Body */}
            <div className="px-6 md:px-8 py-8 md:py-10 min-h-[330px]">
              {/* Scanning */}
              {isScanning && (
                <div className="text-center py-10">
                  <div className="w-12 h-12 mx-auto mb-6 rounded-full border-[3px] border-background-200 border-t-primary-500 animate-spin" />
                  <p className="text-sm text-foreground-500 font-medium min-h-[26px] mb-6">{scanText}</p>
                  <div className="h-[5px] max-w-[320px] mx-auto bg-background-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-300"
                      style={{ width: `${scanProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Results */}
              {showResults && (
                <div ref={resultsRef}>
                  {/* Score gauge */}
                  <div className="text-center mb-8">
                    <div className="w-[210px] h-[118px] mx-auto relative">
                      <svg viewBox="0 0 210 118" className="w-full h-full overflow-visible">
                        <path
                          d="M 20 105 A 85 85 0 0 1 190 105"
                          fill="none"
                          stroke="oklch(var(--background-200))"
                          strokeWidth="13"
                          strokeLinecap="round"
                        />
                        <path
                          d="M 20 105 A 85 85 0 0 1 190 105"
                          fill="none"
                          stroke="oklch(var(--primary-500))"
                          strokeWidth="13"
                          strokeLinecap="round"
                          strokeDasharray="283"
                          strokeDashoffset={gaugeOffset}
                          style={{ transition: "stroke-dashoffset 1.6s cubic-bezier(0.4, 0, 0.2, 1)" }}
                        />
                      </svg>
                      <div className="absolute inset-x-0 bottom-[-4px] text-center">
                        <span className="text-[2.6rem] font-bold text-foreground-950 tracking-tight">{score}</span>
                        <span className="text-base text-foreground-400 font-semibold">/100</span>
                      </div>
                    </div>
                    <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-foreground-500 mt-3">Twoja ocena</div>
                    <div className={`inline-block mt-2.5 px-4 py-1.5 rounded-full text-sm font-bold ${scoreInfo.color}`}>
                      {scoreInfo.label}
                    </div>
                  </div>

                  {/* Result cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    {resCards.map((card, i) => (
                      <div
                        key={card.name}
                        className="border border-background-200 rounded-xl p-4 md:p-5 animate-fade-in-up"
                        style={{ animationDelay: `${i * 100}ms` }}
                      >
                        <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-foreground-500 mb-2">{card.name}</div>
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-xl font-bold text-foreground-950 tracking-tight">{card.val}</span>
                        </div>
                        <p className="text-xs text-foreground-500 leading-relaxed mb-3">{card.note}</p>
                        <div className="h-[5px] bg-background-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{
                              width: `${card.meter}%`,
                              transition: "width 1.2s 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Insights */}
                  <div className="border border-background-200 rounded-xl p-5 md:p-6 bg-background-100/50">
                    <h3 className="text-sm font-bold text-foreground-950 mb-4">Rekomendacje</h3>
                    {insights.map((ins, i) => (
                      <div
                        key={i}
                        className="flex gap-3 py-2.5 border-b border-background-200/60 last:border-0 animate-fade-in-up"
                        style={{ animationDelay: `${(i + 4) * 100}ms` }}
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0 mt-2" />
                        <p
                          className="text-sm text-foreground-600 leading-[1.6]"
                          dangerouslySetInnerHTML={{ __html: ins }}
                        />
                      </div>
                    ))}
                  </div>

                  <p className="text-[11px] text-foreground-400 text-center mt-4 leading-relaxed">
                    Powyższa analiza ma charakter ogólny i nie stanowi porady inwestycyjnej.
                    Decyzje finansowe podejmij po konsultacji z doradcą.
                  </p>
                </div>
              )}

              {/* Steps */}
              {!isScanning && !showResults && (
                <>
                  {/* Step 1 */}
                  {step === 1 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        W jakim jest Pan/Pani przedziale wiekowym?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">Wiek wpływa na horyzont planowania finansowego.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {[
                          { val: 1, label: "18–29 lat" },
                          { val: 2, label: "30–44 lata" },
                          { val: 3, label: "45–59 lat" },
                          { val: 4, label: "60+ lat" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => handleSelect("age", opt.val)}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] text-left text-[15px] font-semibold transition-all duration-200 cursor-pointer ${
                              answers.age === opt.val
                                ? "border-primary-500 bg-primary-50 text-foreground-950"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                            }`}
                          >
                            <span className={`w-[19px] h-[19px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                              answers.age === opt.val ? "border-primary-500" : "border-background-300"
                            }`}>
                              {answers.age === opt.val && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                            </span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2 */}
                  {step === 2 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        Jaka część dochodu zostaje co miesiąc?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">Po opłaceniu wszystkich rachunków i stałych wydatków.</p>
                      <div className="mb-5">
                        <div className="text-[2.2rem] font-bold text-primary-500 text-center mb-5 tracking-tight">
                          {answers.savingsPct ?? 10}
                          <span className="text-xl text-foreground-400 font-semibold"> %</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={50}
                          step={5}
                          value={answers.savingsPct ?? 10}
                          onChange={(e) => handleSelect("savingsPct", parseInt(e.target.value))}
                          className="w-full h-[6px] rounded-full appearance-none bg-background-200 outline-none accent-primary-500"
                          style={{
                            background: `linear-gradient(to right, oklch(var(--primary-500)) 0%, oklch(var(--primary-500)) ${((answers.savingsPct ?? 10) / 50) * 100}%, oklch(var(--background-200)) ${((answers.savingsPct ?? 10) / 50) * 100}%, oklch(var(--background-200)) 100%)`,
                          }}
                        />
                        <div className="flex justify-between text-[11px] font-semibold text-foreground-400 mt-2.5">
                          <span>0%</span>
                          <span>25%</span>
                          <span>50%+</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3 */}
                  {step === 3 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        Na jak długo wystarczyłyby oszczędności?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">W razie nagłej utraty źródła dochodu.</p>
                      <div className="flex flex-col gap-2.5">
                        {[
                          { val: 1, label: "Poniżej 1 miesiąca" },
                          { val: 2, label: "1–3 miesiące" },
                          { val: 3, label: "3–6 miesięcy" },
                          { val: 4, label: "Ponad 6 miesięcy" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => handleSelect("cushion", opt.val)}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] text-left text-[15px] font-semibold transition-all duration-200 cursor-pointer ${
                              answers.cushion === opt.val
                                ? "border-primary-500 bg-primary-50 text-foreground-950"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                            }`}
                          >
                            <span className={`w-[19px] h-[19px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                              answers.cushion === opt.val ? "border-primary-500" : "border-background-300"
                            }`}>
                              {answers.cushion === opt.val && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                            </span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 4 */}
                  {step === 4 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        Czy oszczędności na siebie pracują?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">Lokaty, obligacje, fundusze, nieruchomości, IKE/IKZE.</p>
                      <div className="flex flex-col gap-2.5">
                        {[
                          { val: 1, label: "Nie — środki pozostają na koncie" },
                          { val: 2, label: "Tylko lokaty lub konto oszczędnościowe" },
                          { val: 3, label: "Częściowo inwestuję" },
                          { val: 4, label: "Mam zdywersyfikowany portfel" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => handleSelect("investments", opt.val)}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] text-left text-[15px] font-semibold transition-all duration-200 cursor-pointer ${
                              answers.investments === opt.val
                                ? "border-primary-500 bg-primary-50 text-foreground-950"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                            }`}
                          >
                            <span className={`w-[19px] h-[19px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                              answers.investments === opt.val ? "border-primary-500" : "border-background-300"
                            }`}>
                              {answers.investments === opt.val && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                            </span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 5 */}
                  {step === 5 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        Jak wygląda zabezpieczenie na przyszłość?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">Emerytura oraz ochrona zdrowia i bliskich.</p>
                      <div className="flex flex-col gap-2.5">
                        {[
                          { val: 1, label: "Jeszcze się tym nie zajmowałem(am)" },
                          { val: 2, label: "Tylko ZUS / podstawowe ubezpieczenie" },
                          { val: 3, label: "Odkładam dodatkowo lub mam polisę" },
                          { val: 4, label: "Pełne zabezpieczenie: emerytura i ochrona" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => handleSelect("security", opt.val)}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] text-left text-[15px] font-semibold transition-all duration-200 cursor-pointer ${
                              answers.security === opt.val
                                ? "border-primary-500 bg-primary-50 text-foreground-950"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                            }`}
                          >
                            <span className={`w-[19px] h-[19px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                              answers.security === opt.val ? "border-primary-500" : "border-background-300"
                            }`}>
                              {answers.security === opt.val && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                            </span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 6 */}
                  {step === 6 && (
                    <div className="animate-fade-in-up">
                      <div className="text-xl md:text-[1.35rem] font-bold text-foreground-950 tracking-tight mb-1.5">
                        Najważniejszy cel finansowy?
                      </div>
                      <p className="text-sm text-foreground-500 mb-6">Na tym skupi się podsumowanie analizy.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {[
                          { val: 1, label: "Mieszkanie / dom" },
                          { val: 2, label: "Spokojna emerytura" },
                          { val: 3, label: "Pomnażanie kapitału" },
                          { val: 4, label: "Bezpieczeństwo rodziny" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => handleSelect("goal", opt.val)}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] text-left text-[15px] font-semibold transition-all duration-200 cursor-pointer ${
                              answers.goal === opt.val
                                ? "border-primary-500 bg-primary-50 text-foreground-950"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                            }`}
                          >
                            <span className={`w-[19px] h-[19px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                              answers.goal === opt.val ? "border-primary-500" : "border-background-300"
                            }`}>
                              {answers.goal === opt.val && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                            </span>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-7">
                    <button
                      onClick={prevStep}
                      disabled={step === 1}
                      className="text-sm font-semibold text-foreground-500 hover:text-foreground-950 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer px-1 py-3"
                    >
                      <i className="ri-arrow-left-line mr-1" />
                      Wstecz
                    </button>
                    <button
                      onClick={nextStep}
                      disabled={!canProceed}
                      className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer text-sm"
                    >
                      {step === totalSteps ? "Zobacz wynik" : "Dalej"}
                      <i className={`ri-${step === totalSteps ? "check-line" : "arrow-right-line"}`} />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Anon footer */}
            {!showResults && !isScanning && (
              <div className="flex items-center justify-center gap-2 text-[11px] text-foreground-400 font-medium pb-5">
                <i className="ri-shield-check-line text-sm" />
                Anonimowo · Brak zapisu danych · Bez zobowiązań
              </div>
            )}
          </div>

          {/* Final CTA after results */}
          {showResults && (
            <div className="mt-7 bg-foreground-950 rounded-2xl p-8 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                Omówmy Twój wynik osobiście
              </h2>
              <p className="text-sm text-white/60 max-w-[440px] mx-auto mb-6">
                Umów bezpłatną konsultację — przejdziemy przez Twój wynik i zaproponujemy konkretne kroki.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-bold rounded-full hover:bg-primary-600 hover:scale-105 active:scale-95 transition-all duration-300 text-lg tracking-wide cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {["Bezpłatna konsultacja", "Indywidualna analiza", "Bez zobowiązań"].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}